import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CallMemo } from '@work4all/models/lib/Classes/CallMemo.entity';
import { SalesOpportunities } from '@work4all/models/lib/Classes/SalesOpportunities.entity';
import { Task } from '@work4all/models/lib/Classes/Task.entity';
import { Ticket } from '@work4all/models/lib/Classes/Ticket.entity';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { useOpenMask } from '../../../containers/mask-overlays/mask-overlay/hooks/use-open-mask';
import { MaskTemplateEntity } from '../../../containers/mask-overlays/mask-overlay/types';
import { useLeftAreaVisibleFilters } from '../../../hooks/use-left-area-visible-filters';
import { settings, useSetting } from '../../../settings';
import { PersonalAndGeneralViewV8 } from '../../filters/PersonalAndGeneralView/v8';
import {
  EntityTableLayoutV8,
  EntityTableLayoutV8Props,
} from '../table/EntityTableLayoutV8';
import {
  TableLayoutContext,
  TableLayoutContextValue,
  useTableLayoutState,
} from '../table-layout';
import { useDeleteEntitiesToolbarConfig } from '../use-delete-entities-toolbar-config';
import { useTableHandlersV8 } from '../use-table-handlers-v8';

import { EntityPreview } from './EntityPreview';
import {
  IUseEntityDataTableV8Options,
  useEntityDataTableV8,
} from './use-entity-data-table-v8';

type TableEntity = Task[] | Ticket[] | CallMemo[] | SalesOpportunities[];

export interface IEntityTableV8<TEntity = unknown>
  extends Omit<
      EntityTableV8Props,
      keyof ReturnType<typeof useEntityDataTableV8>
    >,
    Omit<IUseEntityDataTableV8Options<TEntity, never>, 'schema'> {}

interface EntityTableV8Props extends EntityTableLayoutV8Props {
  entityType: Entities;
  settingsEntityType?: Entities | string;
  selectedEntities?: TableEntity;
  template?: MaskTemplateEntity;
  /**
   * This is for override actions and areas when you want to replace them not only add.
   */
  overrides?: Partial<EntityTableLayoutV8Props>;
}

export function EntityTableV8(props: EntityTableV8Props) {
  const {
    entityType,
    settingsEntityType,
    actions,
    areas,
    template,
    overrides,
    table,
    selectedEntities,
    layout: layoutProp,
    ...tableProps
  } = props;

  const { t } = useTranslation();

  const [layoutState, setLayoutState] = useTableLayoutState();
  const layout = layoutProp ?? layoutState;
  const layoutStateContextValue = useMemo<TableLayoutContextValue>(() => {
    return [layout, setLayoutState];
  }, [layout, setLayoutState]);

  const onOpenMask = useOpenMask({ entityType });
  const tableHandlers = useTableHandlersV8(
    table,
    entityType,
    onOpenMask,
    template
  );
  const deleteConfig = useDeleteEntitiesToolbarConfig({
    entityType,
    selectedEntities,
  });

  const savedTableConfig = useSetting(
    settings.tableConfig({
      entityType: settingsEntityType ?? entityType,
      layout,
    })
  );

  const { set: setPreviewVisible } = useSetting(
    settings.tableRightAreaVisible({
      entityType,
    })
  );

  const { action: leftAreaVisibleFiltersAction, isFilterVisible } =
    useLeftAreaVisibleFilters({
      entity: entityType,
      filters: [
        { id: 'personal', label: t('COMMON.PERSONAL_FILTERS') },
        { id: 'general', label: t('COMMON.GENERAL_FILTERS') },
      ],
    });

  return (
    <TableLayoutContext value={layoutStateContextValue}>
      <EntityTableLayoutV8
        table={table}
        layout={layout}
        areas={{
          left: {
            content: (
              <PersonalAndGeneralViewV8
                entity={entityType}
                hidePersonalFilters={!isFilterVisible('personal')}
                hideGeneralFilters={!isFilterVisible('general')}
                table={table}
              />
            ),
            resizable: true,
            title: t('COMMON.VIEW'),
            collapsible: true,
            actions: [leftAreaVisibleFiltersAction],
          },
          right: selectedEntities.length > 0 && {
            content: areas?.right?.content ?? (
              <EntityPreview
                entries={selectedEntities}
                entityType={entityType}
                onCloseClick={() => table.resetRowSelection()}
                onVisibilityToggle={(e) => setPreviewVisible(e)}
                custom={actions?.custom}
              />
            ),
          },
          ...areas,
        }}
        actions={{
          add: tableHandlers.create,
          edit: tableHandlers.edit,
          resetColumns: savedTableConfig.delete,
          remove: deleteConfig,
          convert: tableHandlers.convert
            ? { exclude: entityType, handler: tableHandlers.convert }
            : undefined,
          createWidget: true,
          clone: tableHandlers.clone,
          openInNewTab: tableHandlers.openInNewTab,
          ...actions,
        }}
        {...tableProps}
        {...overrides}
      />
    </TableLayoutContext>
  );
}
